import { ISnippet } from '@principle-theorem/editor';
import {
  INotification,
  IPopupNotification,
} from '@principle-theorem/notifications';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import {
  BrandCollection,
  CollectionGroup,
  EventType,
  IBrand,
  ICalendarEvent,
  ICalendarEventSchedule,
  IEventable,
  IPractice,
  IProviderData,
  IRole,
  IStaffer,
  ITeam,
  ITreatmentPlan,
  IUser,
  IUserHasPermissionPair,
  OrganisationCollection,
  Permission,
  RootCollection,
  ServiceTypeModality,
  StafferCollection,
  TreatmentPlanStatus,
  isAppointment,
  isCalendarEvent,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  ITimePeriod,
  Query,
  WithRef,
  all$,
  asDocRef,
  asyncForEach,
  collectionGroupQuery,
  initFirestoreModel,
  isChanged$,
  isRefChanged$,
  isSameRef,
  safeCombineLatest,
  subCollection,
  toTimePeriod,
  where,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { MonoTypeOperatorFunction, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CalendarEventSchedule } from '../event/calendar-event-schedule';
import { isParticipant } from '../event/event';
import { OrganisationCache } from '../organisation/organisation-cache';
import { User } from '../user/user';
import { resolveToStaff } from '../user/user-operators';

export class Staffer {
  static init(overrides: AtLeast<IStaffer, 'user'>): IStaffer {
    return {
      teams: [],
      tasks: {
        watching: 0,
        assigned: 0,
      },
      quickChartingConfiguration: {
        conditions: [],
        treatments: [],
        multiTreatments: [],
      },
      providerDetails: [],
      billingEntities: [],
      settings: {},
      ...initFirestoreModel(),
      ...overrides,
    };
  }

  static brandRef(staffer: IReffable<IStaffer>): DocumentReference<IBrand> {
    return Firestore.getParentDocRef(staffer.ref);
  }

  static user$(staffer: IStaffer): Observable<WithRef<IUser>> {
    return OrganisationCache.users.get.doc$(staffer.user.ref);
  }

  static user(staffer: IStaffer): Promise<WithRef<IUser>> {
    return OrganisationCache.users.get.getDoc(staffer.user.ref);
  }

  static roles$(staffer: IStaffer): Observable<WithRef<IRole>[]> {
    return Staffer.user$(staffer).pipe(switchMap((user) => User.roles$(user)));
  }

  static permissions$(staffer: IStaffer): Observable<Permission[]> {
    return Staffer.user$(staffer).pipe(
      switchMap((user: WithRef<IUser>) => User.permissions$(user))
    );
  }

  static practitionersByBrand$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IStaffer>[]> {
    return User.byBrand$(brand).pipe(
      filterByPermission(AppointmentPermissions.AppointmentConduct),
      resolveToStaff(brand),
      map(compact)
    );
  }

  static hasPermissionsByBrand$(
    brand: IReffable<IBrand>,
    permissions: Permission[]
  ): Observable<WithRef<IStaffer>[]> {
    return User.byBrand$(brand).pipe(
      filterByPermissions(permissions),
      resolveToStaff(brand),
      map(compact)
    );
  }

  static byPractice$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IStaffer>[]> {
    const users$: Observable<WithRef<IUser>[]> = User.byPractice$(practice);

    return combineLatest([
      OrganisationCache.brands.doc$(
        asDocRef<IBrand>(Firestore.getParentDocRef(practice.ref))
      ),
      users$,
    ]).pipe(
      switchMap(([brand, users]) => resolveToStaff(brand)(of(users))),
      map(compact)
    );
  }

  static practitionersByPractice$(
    practice: IReffable<IPractice>
  ): Observable<WithRef<IStaffer>[]> {
    const users$: Observable<WithRef<IUser>[]> = User.byPractice$(
      practice
    ).pipe(
      filterByPermission(AppointmentPermissions.AppointmentConduct),
      isChanged$(isSameRef)
    );

    return combineLatest([
      OrganisationCache.brands
        .doc$(asDocRef<IBrand>(Firestore.getParentDocRef(practice.ref)))
        .pipe(isRefChanged$()),
      users$,
    ]).pipe(
      switchMap(([brand, users]) => resolveToStaff(brand)(of(users))),
      map(compact)
    );
  }

  static hasPermission$(
    staffer: IStaffer,
    permission: Permission
  ): Observable<boolean> {
    return Staffer.user$(staffer).pipe(
      switchMap((user: WithRef<IUser>) => User.hasPermission$(user, permission))
    );
  }

  static hasPermissionsByPractice$(
    practice: WithRef<IPractice>,
    permissions: Permission[]
  ): Observable<WithRef<IStaffer>[]> {
    const users$: Observable<WithRef<IUser>[]> = User.byPractice$(
      practice
    ).pipe(filterByPermissions(permissions));

    return combineLatest([
      OrganisationCache.brands.doc$(
        asDocRef<IBrand>(Firestore.getParentDocRef(practice.ref))
      ),
      users$,
    ]).pipe(
      switchMap(([brand, users]) => resolveToStaff(brand)(of(users))),
      map(compact)
    );
  }

  static teams(staffer: IStaffer): Observable<WithRef<ITeam>[]> {
    return safeCombineLatest(
      staffer.teams.map((team) => Firestore.getDoc(team.ref))
    );
  }

  static notificationCol(
    staffer: IReffable<IStaffer>
  ): CollectionReference<INotification> {
    return subCollection<INotification>(
      staffer.ref,
      StafferCollection.Notifications
    );
  }

  static notifications$(
    staffer: IReffable<IStaffer>
  ): Observable<WithRef<INotification>[]> {
    return all$(Staffer.notificationCol(staffer));
  }

  static popupNotificationCol(
    staffer: IReffable<IStaffer>
  ): CollectionReference<IPopupNotification> {
    return subCollection<IPopupNotification>(
      staffer.ref,
      StafferCollection.PopupNotifications
    );
  }

  static popupNotifications$(
    staffer: IReffable<IStaffer>
  ): Observable<WithRef<IPopupNotification>[]> {
    return all$(Staffer.popupNotificationCol(staffer));
  }

  static snippetCol(
    staffer: IReffable<IStaffer>
  ): CollectionReference<ISnippet> {
    return subCollection<ISnippet>(staffer.ref, StafferCollection.Snippets);
  }

  static snippets$(
    staffer: IReffable<IStaffer>
  ): Observable<WithRef<ISnippet>[]> {
    return all$(Staffer.snippetCol(staffer));
  }

  static practices$(staffer: IStaffer): Observable<WithRef<IPractice>[]> {
    return Staffer.user$(staffer).pipe(
      switchMap((user) =>
        combineLatest(
          user.practices.map((ref) => OrganisationCache.practices.doc$(ref))
        )
      )
    );
  }

  static getTreatmentPlansCollectionGroup(
    staffer: WithRef<IStaffer>,
    statuses: TreatmentPlanStatus[]
  ): Query<ITreatmentPlan> {
    return collectionGroupQuery<ITreatmentPlan>(
      CollectionGroup.TreatmentPlans,
      where('practitioner.ref', '==', staffer.ref),
      where('status', 'in', statuses)
    );
  }

  static getProviderData(
    staffer: IStaffer,
    practiceRef: DocumentReference<IPractice>
  ): IProviderData | undefined {
    const dcnzNumber = staffer.metadata?.nz?.providerDetails.dcnzNumber;
    if (dcnzNumber) {
      return {
        practiceRef,
        providerNumber: dcnzNumber,
        providerModality: ServiceTypeModality.NotApplicable,
      };
    }

    const providerData = staffer.providerDetails.find((provider) =>
      isSameRef(provider.practiceRef, practiceRef)
    );
    if (
      !providerData ||
      !providerData.providerModality ||
      !providerData.providerNumber
    ) {
      return;
    }
    return providerData;
  }

  static async getBlockingEvents(
    staffer: IReffable<IStaffer>,
    blockingSchedules: WithRef<ICalendarEventSchedule>[],
    range: ITimePeriod,
    events: WithRef<IEventable>[] = []
  ): Promise<ICalendarEvent[]> {
    const blockingEvents = events
      .filter(
        (event) =>
          isAppointment(event) ||
          (isCalendarEvent(event) &&
            event.isBlocking &&
            event.event.type !== EventType.RosteredOn)
      )
      .filter((event) => isParticipant(event, staffer.ref))
      .map((event) => toTimePeriod(event.event.from, event.event.to));

    const stafferSchedules = blockingSchedules.filter((schedule) =>
      schedule.item.event.participantRefs.some((participantRef) =>
        isSameRef(participantRef, staffer.ref)
      )
    );

    const builtEvents = await asyncForEach(stafferSchedules, (schedule) =>
      CalendarEventSchedule.buildEvents(schedule, range, blockingEvents)
    );

    return builtEvents.flat();
  }

  static async buildEventsFromForecast(
    staff: IReffable<IStaffer>[],
    blockingSchedules: WithRef<ICalendarEventSchedule>[],
    range: ITimePeriod,
    events: WithRef<IEventable>[] = []
  ): Promise<ICalendarEvent[]> {
    const builtEvents = await asyncForEach(staff, (staffMember) =>
      Staffer.getBlockingEvents(staffMember, blockingSchedules, range, events)
    );

    return builtEvents.flat();
  }

  static onlineProfileName(staffer: IStaffer): string {
    return staffer.onlineProfile?.name || staffer.user.name;
  }

  static onlineProfileImageURL(staffer: IStaffer): string | undefined {
    return staffer.onlineProfile?.profileImageURL;
  }
}

export function filterByPermission(
  permission: Permission
): MonoTypeOperatorFunction<WithRef<IUser>[]> {
  return (source: Observable<WithRef<IUser>[]>) =>
    source.pipe(
      switchMap((users) =>
        safeCombineLatest(
          users.map((user) =>
            User.hasPermission$(user, permission).pipe(
              map((hasPermission: boolean) => ({ user, hasPermission }))
            )
          )
        )
      ),
      map((pairs: IUserHasPermissionPair[]) =>
        pairs
          .filter((pair: IUserHasPermissionPair) => pair.hasPermission)
          .map((pair: IUserHasPermissionPair) => pair.user)
      )
    );
}

export function filterByPermissions(
  permissions: Permission[]
): MonoTypeOperatorFunction<WithRef<IUser>[]> {
  return (source: Observable<WithRef<IUser>[]>) =>
    source.pipe(
      switchMap((users) =>
        safeCombineLatest(
          users.map((user) =>
            User.hasPermissions$(user, permissions).pipe(
              map((hasPermission: boolean) => ({ user, hasPermission }))
            )
          )
        )
      ),
      map((pairs: IUserHasPermissionPair[]) =>
        pairs
          .filter((pair: IUserHasPermissionPair) => pair.hasPermission)
          .map((pair: IUserHasPermissionPair) => pair.user)
      )
    );
}

export function isStafferRef(
  ref: DocumentReference<unknown>
): ref is DocumentReference<IStaffer> {
  const segments = ref.path.split('/');
  return (
    segments.length === 6 &&
    String(segments[0]) === String(RootCollection.Organisations) &&
    String(segments[2]) === String(OrganisationCollection.Brands) &&
    String(segments[4]) === String(BrandCollection.Staff)
  );
}
